<app-menu>
  <div class="rm-shell" [attr.data-theme]="themeMode">
    <div class="rm-wrapper mx-auto flex min-h-0 max-w-[1900px] flex-col gap-3 px-4 py-3">
      <header class="rm-toolbar">
        <div class="rm-title-stack min-w-0">
          <div class="rm-eyebrow">Financials / Rate Manager</div>
          <div class="flex min-w-0 flex-wrap items-center gap-3">
            <h1 class="truncate">Rate Manager</h1>
            <span class="rm-readiness-pill" [ngClass]="readinessClass()">
              <i class="fa-solid" [ngClass]="readinessTone === 'error' ? 'fa-circle-xmark' : readinessTone === 'warning' ? 'fa-triangle-exclamation' : readinessTone === 'ready' ? 'fa-circle-check' : 'fa-circle-info'"></i>
              {{ readinessLabel }}
            </span>
          </div>
          <div class="rm-meta-strip">
            <span><strong>{{ selectedTypeLabel }}</strong> type</span>
            <span><strong>{{ zones.length }}</strong> zones</span>
            <span><strong>{{ rates.length }}</strong> visible</span>
            <span><strong>{{ activeRatesCount }}</strong> active</span>
            <span><strong>{{ inactiveRatesCount }}</strong> inactive</span>
            <span><strong>{{ totalRows || rates.length }}</strong> total</span>
          </div>
        </div>

        <div class="rm-toolbar-actions">
          <button type="button" class="rm-button rm-button--ghost" (click)="toggleTheme()" [title]="getThemeLabel() + ' theme'">
            <i [class]="getThemeIcon()"></i>
            <span>{{ getThemeLabel() }}</span>
          </button>
          <button type="button" class="rm-button rm-button--ghost" (click)="loadAll()" [disabled]="!selectedContactUuid || loading.zones || loading.rates">
            <i class="fa-solid fa-rotate" [class.fa-spin]="loading.zones || loading.rates"></i>
            <span>Refresh</span>
          </button>
          <button type="button" class="rm-button rm-button--primary" (click)="newRate()" [disabled]="!selectedContactUuid || !canEdit">
            <i class="fa-solid fa-plus"></i>
            <span>New Rate</span>
          </button>
          <button type="button" class="rm-button rm-button--accent" (click)="saveRate()" [disabled]="!canSaveRate">
            <i class="fa-solid" [ngClass]="loading.save ? 'fa-spinner fa-spin' : 'fa-floppy-disk'"></i>
            <span>{{ selectedRateUuid ? 'Save Rate' : 'Create Rate' }}</span>
          </button>
        </div>
      </header>

      <div class="rm-alert rm-alert--error" *ngIf="errorMessage">
        <i class="fa-solid fa-triangle-exclamation"></i>
        <span>{{ errorMessage }}</span>
      </div>

      <main class="rm-work-grid min-h-0" [class.is-side-collapsed]="sidePanelCollapsed && rates.length">
        <aside class="rm-panel rm-side-panel min-h-0">
          <button type="button" class="rm-side-rail" (click)="showSidePanel()" title="Show filters">
            <i class="fa-solid fa-sliders"></i>
            <span>Filters</span>
          </button>
          <div class="rm-side-content">
          <div class="rm-panel-header">
            <div>
              <div class="rm-eyebrow">Scope</div>
              <h2>Contact</h2>
            </div>
            <div class="rm-panel-header-actions">
              <span class="rm-mini-status">{{ selectedTypeLabel }}</span>
              <button type="button" class="rm-icon-button rm-side-toggle" (click)="toggleSidePanel()" title="Hide filters" [disabled]="!rates.length">
                <i class="fa-solid fa-chevron-left"></i>
              </button>
            </div>
          </div>

          <section class="rm-control-list">
            <div class="rm-mode-toggle">
              <button type="button" [class.is-active]="contactType === 'carrier'" (click)="setContactType('carrier')">
                <i class="fa-solid fa-truck-fast"></i>
                <span>Carrier</span>
              </button>
              <button type="button" [class.is-active]="contactType === 'customer'" (click)="setContactType('customer')">
                <i class="fa-solid fa-building-user"></i>
                <span>Customer</span>
              </button>
            </div>

            <label class="rm-field rm-contact-field">
              <span>Contact</span>
              <div class="rm-contact-combobox">
                <input type="text" [formControl]="contactSearchControl" [placeholder]="contactSearchPlaceholder" (focus)="contactOptionsOpen = true" (blur)="closeContactOptionsSoon()">
                <div class="rm-contact-options" *ngIf="contactOptionsOpen">
                  <ng-container *ngIf="contactOptions$ | async as contactOptions">
                    <button type="button" *ngFor="let contact of contactOptions" (mousedown)="$event.preventDefault()" (click)="onContactSelected(contact)">
                      <span>{{ contact.label }}</span>
                      <small>{{ contact.value }}</small>
                    </button>
                    <div class="rm-contact-empty" *ngIf="contactSearchControl.value.length > 2 && !contactOptions.length">No contacts found.</div>
                  </ng-container>
                </div>
              </div>
            </label>
          </section>

          <form class="rm-filter-card" [formGroup]="filterForm" (ngSubmit)="loadRates(true)">
            <div class="rm-card-head">
              <div>
                <div class="rm-eyebrow">Filters</div>
                <h3>Freight Rates</h3>
              </div>
              <span class="rm-mini-status">{{ pageStart }}-{{ pageEnd }}</span>
            </div>

            <div class="rm-field-grid">
              <label class="rm-field">
                <span>Status</span>
                <select formControlName="active">
                  <option value="">Any</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </label>

              <label class="rm-field">
                <span>Effective On</span>
                <input type="date" formControlName="effective_on">
              </label>

              <label class="rm-field">
                <span>Method</span>
                <select formControlName="rate_method">
                  <option value="">Any</option>
                  <option *ngFor="let option of rateMethodOptions" [value]="option.value" [disabled]="!isMethodAvailable(option)">{{ option.label }}</option>
                </select>
              </label>

              <label class="rm-field">
                <span>Unit</span>
                <select formControlName="rate_unit">
                  <option value="">Any</option>
                  <option *ngFor="let option of rateUnitOptions" [value]="option.value" [disabled]="!isUnitAvailable(option)">{{ option.label }}</option>
                </select>
              </label>

              <label class="rm-field">
                <span>Delivery Type</span>
                <select formControlName="delivery_type">
                  <option value="">Any</option>
                  <option *ngFor="let option of deliveryTypeOptions" [value]="option.value">{{ option.label }}</option>
                </select>
              </label>

              <label class="rm-field">
                <span>Currency</span>
                <select formControlName="currency">
                  <option value="">Any</option>
                  <option *ngFor="let currency of currencyOptions" [value]="currency">{{ currency }}</option>
                </select>
              </label>

              <label class="rm-field rm-field--wide">
                <span>Origin Zone</span>
                <select formControlName="origin_zone_uuid">
                  <option value="">Any</option>
                  <option *ngFor="let zone of originZones; trackBy: trackByZone" [value]="zone.uuid">{{ zone.label }}</option>
                </select>
              </label>

              <label class="rm-field rm-field--wide">
                <span>Destination Zone</span>
                <select formControlName="destination_zone_uuid">
                  <option value="">Any</option>
                  <option *ngFor="let zone of destinationZones; trackBy: trackByZone" [value]="zone.uuid">{{ zone.label }}</option>
                </select>
              </label>

              <label class="rm-field">
                <span>Limit</span>
                <input type="number" min="10" step="10" formControlName="limit">
              </label>
            </div>

            <div class="rm-actions-row">
              <button type="button" class="rm-button rm-button--ghost" (click)="resetFilters()" [disabled]="!selectedContactUuid || loading.rates">
                <i class="fa-solid fa-eraser"></i>
                <span>Reset</span>
              </button>
              <button type="submit" class="rm-button rm-button--primary" [disabled]="!selectedContactUuid || loading.rates">
                <i class="fa-solid" [ngClass]="loading.rates ? 'fa-spinner fa-spin' : 'fa-filter'"></i>
                <span>Apply</span>
              </button>
            </div>
          </form>

          <section class="rm-readiness-card" [ngClass]="readinessClass()">
            <div class="rm-readiness-head">
              <div>
                <span>Readiness</span>
                <strong>{{ readinessLabel }}</strong>
              </div>
              <b>{{ readinessPercent }}%</b>
            </div>
            <div class="rm-progress"><span [style.width.%]="readinessPercent"></span></div>
            <div class="rm-readiness-grid">
              <span><strong>{{ zones.length }}</strong> zones</span>
              <span><strong>{{ rates.length }}</strong> rates</span>
              <span><strong>{{ activeRatesCount }}</strong> active</span>
              <span><strong>{{ issueList.length }}</strong> checks</span>
            </div>
            <p>{{ submitDisabledReason }}</p>
          </section>

          <section class="rm-issues-card">
            <div class="rm-issues-head">
              <span>Data Checks</span>
              <strong>{{ issueList.length }}</strong>
            </div>
            <div class="rm-issue-list" *ngIf="issueList.length; else cleanIssues">
              <article class="rm-issue" *ngFor="let issue of visibleIssues; trackBy: trackByIssue" [ngClass]="issueClass(issue)">
                <i class="fa-solid" [ngClass]="issue.severity === 'error' ? 'fa-circle-xmark' : 'fa-triangle-exclamation'"></i>
                <div><span>{{ issue.message }}</span></div>
              </article>
            </div>
            <ng-template #cleanIssues>
              <div class="rm-clean-state">
                <i class="fa-solid fa-circle-check"></i>
                <span>No blocking checks.</span>
              </div>
            </ng-template>
          </section>
          </div>
        </aside>

        <section class="rm-panel rm-workbench min-h-0">
          <div class="rm-tabs">
            <button type="button" class="rm-tab" [class.is-active]="activeTab === 'rates'" (click)="setActiveTab('rates')">
              <i class="fa-solid fa-table-cells"></i>
              <span>Rates</span>
            </button>
            <button type="button" class="rm-tab" [class.is-active]="activeTab === 'editor'" (click)="setActiveTab('editor')">
              <i class="fa-solid fa-pen-to-square"></i>
              <span>Editor</span>
            </button>
            <button type="button" class="rm-tab" [class.is-active]="activeTab === 'import'" (click)="setActiveTab('import')">
              <i class="fa-solid fa-file-import"></i>
              <span>Import</span>
            </button>
            <button type="button" class="rm-tab" [class.is-active]="activeTab === 'json'" (click)="setActiveTab('json')">
              <i class="fa-solid fa-code"></i>
              <span>JSON</span>
            </button>
          </div>

          <div class="rm-workbench-body" [ngSwitch]="activeTab">
            <section *ngSwitchCase="'rates'" class="rm-tab-panel rm-tab-panel--rates">
              <div class="rm-preview-head">
                <div class="min-w-0">
                  <div class="rm-eyebrow">Selected Scope</div>
                  <h2 class="truncate">{{ selectedContactName }}</h2>
                  <p>{{ pageNote || submitDisabledReason }}</p>
                </div>
                <div class="rm-row-summary">
                  <span><strong>{{ pageStart }}</strong> start</span>
                  <span><strong>{{ pageEnd }}</strong> end</span>
                  <span><strong>{{ totalRows || rates.length }}</strong> total</span>
                  <button type="button" class="rm-button rm-button--ghost rm-button--compact" (click)="pageBack()" [disabled]="!canPageBack">
                    <i class="fa-solid fa-chevron-left"></i>
                    <span>Prev</span>
                  </button>
                  <button type="button" class="rm-button rm-button--ghost rm-button--compact" (click)="pageForward()" [disabled]="!canPageForward">
                    <span>Next</span>
                    <i class="fa-solid fa-chevron-right"></i>
                  </button>
                </div>
              </div>

              <div class="rm-table-wrap" *ngIf="rates.length; else noRates">
                <div class="rm-rate-grid" role="table" aria-label="Freight rates">
                  <div class="rm-rate-grid-row rm-rate-grid-row--head" role="row">
                    <div role="columnheader">Action</div>
                    <div role="columnheader">Status</div>
                    <div role="columnheader">Origin</div>
                    <div role="columnheader">Destination</div>
                    <div role="columnheader">Dates</div>
                    <div role="columnheader">Method</div>
                    <div role="columnheader">Unit</div>
                    <div role="columnheader">Weight</div>
                    <div role="columnheader">Length</div>
                    <div role="columnheader">Delivery</div>
                    <div role="columnheader">Transit</div>
                    <div role="columnheader">Rate</div>
                    <div role="columnheader">Min / Max</div>
                  </div>

                  <div
                    class="rm-rate-grid-row rm-rate-grid-row--data"
                    role="row"
                    tabindex="0"
                    *ngFor="let row of rates; trackBy: trackByRate"
                    (click)="selectRate(row)"
                    (keydown.enter)="selectRate(row)"
                    (keydown.space)="selectRate(row); $event.preventDefault()"
                    [class.is-selected]="rateUuid(row) === selectedRateUuid">
                    <div class="rm-rate-grid-cell" role="cell">
                      <button type="button" class="rm-icon-button" (click)="selectRate(row); $event.stopPropagation()" title="Edit rate">
                        <i class="fa-solid fa-pen"></i>
                      </button>
                    </div>
                    <div class="rm-rate-grid-cell" role="cell"><span class="rm-status-pill" [ngClass]="statusClass(row)">{{ activeLabel(row) }}</span></div>
                    <div class="rm-rate-grid-cell" role="cell">{{ originZoneLabel(row) }}</div>
                    <div class="rm-rate-grid-cell" role="cell">{{ destinationZoneLabel(row) }}</div>
                    <div class="rm-rate-grid-cell" role="cell">{{ formatDate($any(row)['start_date']) }} / {{ formatDate($any(row)['end_date']) }}</div>
                    <div class="rm-rate-grid-cell" role="cell">{{ methodLabel($any(row)['rate_method']) }}</div>
                    <div class="rm-rate-grid-cell" role="cell">{{ unitLabel($any(row)['rate_unit']) }}</div>
                    <div class="rm-rate-grid-cell" role="cell">{{ weightRange(row) }}</div>
                    <div class="rm-rate-grid-cell" role="cell">{{ lengthRange(row) }}</div>
                    <div class="rm-rate-grid-cell" role="cell">{{ deliveryTypeLabel(row) }}</div>
                    <div class="rm-rate-grid-cell" role="cell">{{ transitLabel(row) }}</div>
                    <div class="rm-rate-grid-cell" role="cell"><strong>{{ rateAmount(row) }}</strong></div>
                    <div class="rm-rate-grid-cell" role="cell">{{ minMaxAmount(row) }}</div>
                  </div>
                </div>
              </div>

              <ng-template #noRates>
                <div class="rm-empty rm-empty--large">
                  <i class="fa-solid" [ngClass]="loading.rates ? 'fa-spinner fa-spin' : 'fa-table-cells'"></i>
                  <strong>{{ loading.rates ? 'Loading rates' : 'No rates loaded.' }}</strong>
                  <span>{{ selectedContactUuid ? 'Try a different filter set.' : 'Select a contact to load rates.' }}</span>
                </div>
              </ng-template>
            </section>

            <section *ngSwitchCase="'editor'" class="rm-tab-panel">
              <form class="rm-editor" [formGroup]="editorForm" (ngSubmit)="saveRate()" *ngIf="selectedContactUuid; else selectContactForEditor">
                <div class="rm-preview-head">
                  <div class="min-w-0">
                    <div class="rm-eyebrow">Rate Editor</div>
                    <h2 class="truncate">{{ selectedRateUuid || 'New rate' }}</h2>
                    <p>{{ selectedContactName }}</p>
                  </div>
                  <div class="rm-toolbar-actions">
                    <button type="button" class="rm-button rm-button--ghost" (click)="newRate()">
                      <i class="fa-solid fa-plus"></i>
                      <span>New</span>
                    </button>
                    <button type="button" class="rm-button rm-button--warning" (click)="deactivateRate()" [disabled]="!selectedRateUuid || !canDelete || loading.delete">
                      <i class="fa-solid" [ngClass]="loading.delete ? 'fa-spinner fa-spin' : 'fa-ban'"></i>
                      <span>Deactivate</span>
                    </button>
                    <button type="submit" class="rm-button rm-button--accent" [disabled]="!canSaveRate">
                      <i class="fa-solid" [ngClass]="loading.save ? 'fa-spinner fa-spin' : 'fa-floppy-disk'"></i>
                      <span>Save</span>
                    </button>
                  </div>
                </div>

                <div class="rm-editor-grid">
                  <label class="rm-field">
                    <span>Start Date</span>
                    <input type="date" formControlName="start_date">
                  </label>
                  <label class="rm-field">
                    <span>End Date</span>
                    <input type="date" formControlName="end_date">
                  </label>
                  <label class="rm-field">
                    <span>Method</span>
                    <select formControlName="rate_method">
                      <option *ngFor="let option of rateMethodOptions" [value]="option.value" [disabled]="!isMethodAvailable(option)">{{ option.label }}</option>
                    </select>
                  </label>
                  <label class="rm-field">
                    <span>Unit</span>
                    <select formControlName="rate_unit">
                      <option *ngFor="let option of rateUnitOptions" [value]="option.value" [disabled]="!isUnitAvailable(option)">{{ option.label }}</option>
                    </select>
                  </label>
                  <label class="rm-field rm-field--wide">
                    <span>Origin Zone</span>
                    <select formControlName="origin_zone_uuid">
                      <option value="">Select origin</option>
                      <option *ngFor="let zone of originZones; trackBy: trackByZone" [value]="zone.uuid">{{ zone.label }}</option>
                    </select>
                  </label>
                  <label class="rm-field rm-field--wide">
                    <span>Destination Zone</span>
                    <select formControlName="destination_zone_uuid">
                      <option value="">Select destination</option>
                      <option *ngFor="let zone of destinationZones; trackBy: trackByZone" [value]="zone.uuid">{{ zone.label }}</option>
                    </select>
                  </label>
                  <label class="rm-field">
                    <span>Weight Start</span>
                    <input type="number" step="0.0001" formControlName="weight_start">
                  </label>
                  <label class="rm-field">
                    <span>Weight End</span>
                    <input type="number" step="0.0001" formControlName="weight_end">
                  </label>
                  <label class="rm-field">
                    <span>Length Start</span>
                    <input type="number" step="0.0001" formControlName="length_start">
                  </label>
                  <label class="rm-field">
                    <span>Length End</span>
                    <input type="number" step="0.0001" formControlName="length_end">
                  </label>
                  <label class="rm-field">
                    <span>Delivery Type</span>
                    <select formControlName="delivery_type">
                      <option value="">None</option>
                      <option *ngFor="let option of deliveryTypeOptions" [value]="option.value">{{ option.label }}</option>
                    </select>
                  </label>
                  <label class="rm-field">
                    <span>{{ contactType === 'carrier' ? 'Transit Time' : 'Transit Days' }}</span>
                    <input type="number" step="1" formControlName="transit_days">
                  </label>
                  <label class="rm-field">
                    <span>Rate</span>
                    <input type="number" step="0.0001" formControlName="rate">
                  </label>
                  <label class="rm-field">
                    <span>Currency</span>
                    <select formControlName="currency">
                      <option *ngFor="let currency of currencyOptions" [value]="currency">{{ currency }}</option>
                    </select>
                  </label>
                  <label class="rm-field">
                    <span>Min Rate</span>
                    <input type="number" step="0.01" formControlName="min_rate">
                  </label>
                  <label class="rm-field">
                    <span>Max Rate</span>
                    <input type="number" step="0.01" formControlName="max_rate">
                  </label>
                  <label class="rm-field">
                    <span>LF Overage Threshold</span>
                    <input type="number" step="0.0001" formControlName="linear_feet_overage_threshold">
                  </label>
                  <label class="rm-field">
                    <span>LF Overage Weight</span>
                    <input type="number" step="0.0001" formControlName="linear_feet_overage_weight">
                  </label>
                  <label class="rm-check-row rm-field--wide">
                    <input type="checkbox" class="rm-check" formControlName="is_rate_active">
                    <span>
                      <strong>Active</strong>
                      <small>Delete deactivates the row.</small>
                    </span>
                  </label>
                </div>
              </form>

              <ng-template #selectContactForEditor>
                <div class="rm-empty rm-empty--large">
                  <i class="fa-solid fa-address-card"></i>
                  <strong>No contact selected.</strong>
                  <span>Select a carrier or customer before editing rates.</span>
                </div>
              </ng-template>
            </section>

            <section *ngSwitchCase="'import'" class="rm-tab-panel">
              <div class="rm-preview-head">
                <div class="min-w-0">
                  <div class="rm-eyebrow">Import</div>
                  <h2 class="truncate">{{ importFileName || 'CSV / JSON rows' }}</h2>
                  <p>{{ selectedContactName }}</p>
                </div>
                <div class="rm-toolbar-actions">
                  <input id="rate-manager-import-file" class="rm-file-input" type="file" accept=".csv,text/csv,.json,application/json" (change)="onImportFileSelected($event)">
                  <label class="rm-button rm-button--primary" for="rate-manager-import-file">
                    <i class="fa-solid fa-file-arrow-up"></i>
                    <span>Upload</span>
                  </label>
                  <button type="button" class="rm-button rm-button--primary" (click)="previewImport()" [disabled]="!canPreviewImport">
                    <i class="fa-solid" [ngClass]="loading.importPreview ? 'fa-spinner fa-spin' : 'fa-eye'"></i>
                    <span>Preview</span>
                  </button>
                  <button type="button" class="rm-button rm-button--accent" (click)="commitImport()" [disabled]="!canCommitImport">
                    <i class="fa-solid" [ngClass]="loading.importCommit ? 'fa-spinner fa-spin' : 'fa-cloud-arrow-up'"></i>
                    <span>Commit</span>
                  </button>
                </div>
              </div>

              <label class="rm-field rm-import-textarea">
                <span>Import Body</span>
                <textarea [(ngModel)]="csvText" [ngModelOptions]="{ standalone: true }" rows="12" placeholder="start_date,end_date,rate_method,rate_unit,..."></textarea>
              </label>

              <div class="rm-split-json">
                <section>
                  <div class="rm-code-head">
                    <div>
                      <div class="rm-eyebrow">Preview</div>
                      <h2>Validation</h2>
                    </div>
                  </div>
                  <pre class="rm-json">{{ importPreviewJson }}</pre>
                </section>
                <section>
                  <div class="rm-code-head">
                    <div>
                      <div class="rm-eyebrow">Commit</div>
                      <h2>Result</h2>
                    </div>
                  </div>
                  <pre class="rm-json">{{ importCommitJson }}</pre>
                </section>
              </div>
            </section>

            <section *ngSwitchCase="'json'" class="rm-tab-panel">
              <div class="rm-split-json">
                <section>
                  <div class="rm-code-head">
                    <div>
                      <div class="rm-eyebrow">Selected Rate</div>
                      <h2>Raw Row</h2>
                    </div>
                  </div>
                  <pre class="rm-json">{{ selectedRateJson }}</pre>
                </section>
                <section>
                  <div class="rm-code-head">
                    <div>
                      <div class="rm-eyebrow">API</div>
                      <h2>Last Response</h2>
                    </div>
                  </div>
                  <pre class="rm-json">{{ lastResponseJson }}</pre>
                </section>
              </div>
            </section>
          </div>
        </section>
      </main>
    </div>
  </div>
</app-menu>
